/**
 * The trip's arithmetic, in whole numbers (never a float): km are tenths, rates are millionths of
 * a euro per km, amounts are cents. The server's figure is the one stored; this is the preview.
 */

/** Km as typed («142», «140,5», «140.5»): at most one decimal, above zero. Null otherwise. */
export function parseKm(input: string): string | null {
  const text = input.trim().replace(',', '.');
  if (!/^\d{1,5}(\.\d)?$/.test(text)) return null;
  const tenths = kmTenths(text);
  return tenths > 0n ? `${(tenths / 10n).toString()}.${(tenths % 10n).toString()}` : null;
}

function kmTenths(km: string): bigint {
  const [whole = '0', fraction = '0'] = km.split('.');
  return BigInt(whole) * 10n + BigInt(fraction.slice(0, 1) || '0');
}

/** The km claimed: doubled for a round trip, as the server does. */
export function claimedKm(km: string, roundTrip: boolean): string {
  const tenths = kmTenths(km) * (roundTrip ? 2n : 1n);
  return `${(tenths / 10n).toString()}.${(tenths % 10n).toString()}`;
}

function microOf(rate: string): bigint {
  const [whole = '0', fraction = ''] = rate.split('.');
  return BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0').slice(0, 6));
}

/** km × rate, rounded half up to the cent: «43.56». `km` is the claimed km. */
export function tripAmount(km: string, rate: string): string {
  const numerator = kmTenths(km) * microOf(rate);
  const cents = (numerator + 50_000n) / 100_000n;
  return `${(cents / 100n).toString()}.${(cents % 100n).toString().padStart(2, '0')}`;
}

/** An API rate for display: «0.310000» → «0,31». Keeps up to four decimals when needed. */
export function formatRate(rate: string): string {
  const [whole = '0', fraction = ''] = rate.split('.');
  const trimmed = fraction.replace(/0+$/, '').padEnd(2, '0');
  return `${whole},${trimmed}`;
}

/** Km for display, it-IT: «142.0» → «142», «140.5» → «140,5». */
export function formatKm(km: string): string {
  return km.replace(/\.0$/, '').replace('.', ',');
}

/** A rate typed by a person («0,31», «0.3125»): up to six decimals, above zero; else null. */
export function parseRate(input: string): string | null {
  const text = input.trim().replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,6})?$/.test(text)) return null;
  return microOf(text) > 0n ? text : null;
}
